import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ArrowLeft, Plug, Trash2, AlertTriangle, Globe, Copy, Lock,
} from "lucide-react";
import { STEPS, StatusPill } from "./wizard/_shared";
import GeneralStep from "./wizard/GeneralStep";
import AuthSecretsStep from "./wizard/AuthSecretsStep";
import EndpointsStep from "./wizard/EndpointsStep";
import PublishStep from "./wizard/PublishStep";
import AnalyticsStep from "./wizard/AnalyticsStep";

/**
 * 4-step wizard orchestrator for the Connections module (Phase C.2 +
 * C.3). Owns data loading, mutating actions on the parent connection
 * (retire / delete / clone / mark-common), and step navigation.
 *
 * Each step renders its own card content and modal interactions. Per-
 * step files live in ``./wizard/*Step.jsx``.
 */
export default function ConnectionDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("connection.edit");
    const canPublish = hasPermission("connection.publish");
    const canTest = hasPermission("connection.test");
    const canManageSecret = hasPermission("connection.secret.manage");
    const canMarkCommon = hasPermission("connection.publish_common");
    const canDelete = hasPermission("connection.delete");

    const [step, setStep] = useState(searchParams.get("step") || "general");
    const [conn, setConn] = useState(null);
    const [endpoints, setEndpoints] = useState([]);
    const [secrets, setSecrets] = useState([]);
    const [usage, setUsage] = useState({ flow_count: 0, flows: [] });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const sp = new URLSearchParams(searchParams);
        sp.set("step", step);
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line
    }, [step]);

    const load = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setErr(null);
        try {
            const [c, e, s, u] = await Promise.all([
                api.get(`/admin/connections/${id}`),
                api.get(`/admin/connections/${id}/endpoints`),
                api.get(`/admin/connections/${id}/secrets`),
                api.get(`/admin/connections/${id}/usage`),
            ]);
            setConn(c.data);
            setEndpoints(e.data?.items || []);
            setSecrets(s.data?.items || []);
            setUsage(u.data || { flow_count: 0, flows: [] });
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const isPublished = conn?.status === "published";
    const isRetired = conn?.status === "retired";
    const isImmutable = isPublished || isRetired;

    const retire = async () => {
        if (!window.confirm("Retire this connection? It will become read-only and unusable in new flows.")) return;
        try {
            await api.post(`/admin/connections/${id}/retire`);
            toast.success("Connection retired");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this connection permanently? Drafts are deleted; published-in-use connections must be retired instead.")) return;
        try {
            await api.delete(`/admin/connections/${id}`);
            toast.success("Connection deleted");
            nav("/connections");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const toggleCommon = async () => {
        const next = !conn.is_common;
        const msg = next
            ? "Mark as platform-common? This connection will become visible (read-only) to every tenant in the platform."
            : "Remove platform-common flag? Other tenants will lose access on next read.";
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/admin/connections/${id}/mark-common`);
            toast.success(data.is_common ? "Marked as common" : "Common flag removed");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const cloneToDraft = async () => {
        if (!window.confirm("Clone this published connection into a new editable draft? Endpoints are copied but secrets are not — you'll need to re-attach credentials on the new draft.")) return;
        try {
            const { data } = await api.post(`/admin/connections/${id}/clone`);
            toast.success(`New draft created — v${data.version}`);
            nav(`/connections/${data.id}?step=general`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    if (loading) {
        return <div className="text-xs text-white/50" data-testid="connection-detail-loading">Loading…</div>;
    }
    if (err || !conn) {
        return (
            <div className="text-xs text-rose-200 inline-flex items-center gap-2"
                data-testid="connection-detail-error">
                <AlertTriangle className="w-3 h-3" /> {err || "Not found"}
                <Link to="/connections" className="underline opacity-70 ml-2">Back</Link>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="connection-detail-page">
            <div>
                <Link to="/connections" className="text-[11px] text-white/50 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Connections
                </Link>
                <div className="flex items-end justify-between mt-2 gap-4">
                    <div className="min-w-0">
                        <h1 className="text-2xl flex items-center gap-2 flex-wrap">
                            <Plug className="w-5 h-5 opacity-70 shrink-0" strokeWidth={1.5} />
                            <span className="truncate">{conn.name}</span>
                            <StatusPill status={conn.status} />
                            {conn.is_common && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-violet-300/10 border border-violet-300/30 text-violet-200 inline-flex items-center gap-1">
                                    <Globe className="w-3 h-3" /> COMMON
                                </span>
                            )}
                        </h1>
                        <div className="text-[11px] font-mono text-white/45 mt-1 flex items-center gap-3 flex-wrap">
                            <span>{conn.environment}</span>
                            <span>·</span>
                            <span>{conn.client_id}</span>
                            <span>·</span>
                            <span>v{conn.version}</span>
                            {usage.flow_count > 0 && (
                                <>
                                    <span>·</span>
                                    <span className="text-violet-200" data-testid="connection-usage-counter">
                                        used by {usage.flow_count} flow version{usage.flow_count === 1 ? "" : "s"}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                        {isPublished && canEdit && (
                            <button onClick={cloneToDraft}
                                data-testid="connection-clone-btn"
                                className="px-2.5 py-1.5 text-xs border border-sky-300/30 text-sky-200 rounded-md hover:bg-sky-300/10 inline-flex items-center gap-1.5">
                                <Copy className="w-3 h-3" /> Clone to draft
                            </button>
                        )}
                        {canMarkCommon && (
                            <button onClick={toggleCommon}
                                data-testid="connection-mark-common-btn"
                                className="px-2.5 py-1.5 text-xs border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5">
                                <Globe className="w-3 h-3" /> {conn.is_common ? "Remove common" : "Mark common"}
                            </button>
                        )}
                        {!isRetired && canDelete && (
                            <button onClick={retire}
                                data-testid="connection-retire-btn"
                                className="px-2.5 py-1.5 text-xs border border-amber-300/30 text-amber-200 rounded-md hover:bg-amber-300/10">
                                Retire
                            </button>
                        )}
                        {canDelete && (
                            <button onClick={remove}
                                data-testid="connection-delete-btn"
                                className="px-2.5 py-1.5 text-xs border border-rose-300/30 text-rose-200 rounded-md hover:bg-rose-300/10 inline-flex items-center gap-1.5">
                                <Trash2 className="w-3 h-3" /> Delete
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {isPublished && (
                <div className="px-3 py-2 rounded-md border border-emerald-300/20 bg-emerald-300/[0.04] text-[11px] text-emerald-200/90 inline-flex items-center gap-2 w-full"
                    data-testid="connection-published-banner">
                    <Lock className="w-3 h-3" />
                    Published connections are immutable. Use <span className="font-mono">Clone to draft</span> to create a new editable version.
                </div>
            )}

            {/* Step nav — analytics step shown only for published/retired connections. */}
            <div className="flex items-center gap-1 border-b border-white/10" data-testid="connection-wizard-nav">
                {STEPS.filter((s) => !s.publishedOnly || isPublished || isRetired).map((s) => {
                    const Icon = s.icon;
                    const active = step === s.key;
                    return (
                        <button key={s.key} onClick={() => setStep(s.key)}
                            data-testid={`wizard-step-${s.key}`}
                            className={`px-3 py-2 text-xs inline-flex items-center gap-1.5 border-b-2 transition ${
                                active
                                    ? "border-violet-300 text-white"
                                    : "border-transparent text-white/55 hover:text-white"
                            }`}>
                            <Icon className="w-3.5 h-3.5" /> {s.label}
                        </button>
                    );
                })}
            </div>

            {step === "general" && (
                <GeneralStep conn={conn} immutable={isImmutable} canEdit={canEdit} onSaved={load} />
            )}
            {step === "auth" && (
                <AuthSecretsStep
                    conn={conn} secrets={secrets} immutable={isImmutable}
                    canEdit={canEdit} canManageSecret={canManageSecret} canTest={canTest}
                    onSaved={load}
                />
            )}
            {step === "endpoints" && (
                <EndpointsStep
                    conn={conn} endpoints={endpoints} immutable={isImmutable}
                    canEdit={canEdit} canTest={canTest}
                    onChanged={load}
                />
            )}
            {step === "publish" && (
                <PublishStep
                    conn={conn} endpoints={endpoints} usage={usage}
                    canTest={canTest} canPublish={canPublish}
                    onChanged={load}
                />
            )}
            {step === "analytics" && (isPublished || isRetired) && (
                <AnalyticsStep conn={conn} canPublish={canPublish} onChanged={load} />
            )}
        </div>
    );
}
